import { useEffect, useState, useCallback } from "react";
import { Star, Check, Trash2, Loader2, Award, RotateCcw, Megaphone, Users, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const REWARD_STATUS = {
  granted: "bg-emerald-500/15 text-emerald-300",
  pending_approval: "bg-amber-500/15 text-amber-300",
  reversed: "bg-red-500/15 text-red-300",
};

function SubTabs({ tab, setTab }) {
  const tabs = [
    { id: "testimonials", label: "Testimonials", icon: Star },
    { id: "rewards", label: "Referral Rewards", icon: Award },
    { id: "status", label: "Status & Incidents", icon: Megaphone },
  ];
  return (
    <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="admin-trust-subtabs">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`trust-subtab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === t.id ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "text-[#A1A1AA] border border-white/8 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

function Testimonials() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get("/admin/testimonials/pending").then((r) => setPending(r.data.pending)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (t, featured) => {
    try { await api.put(`/admin/testimonials/${t.id}/approve`, { featured, metric: "" });
      toast.success("Approved — now live on the public page"); load();
    } catch (e) { toast.error("Could not approve"); }
  };
  const remove = async (t) => {
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  return (
    <div className="space-y-3" data-testid="admin-testimonials-queue">
      <p className="text-sm text-[#71717A]">{pending.length} testimonial{pending.length !== 1 ? "s" : ""} awaiting approval. Approved ones appear on the public Testimonials page automatically.</p>
      {pending.length === 0 ? (
        <div className="text-center py-10 text-[#52525B]" data-testid="admin-testimonials-empty">No pending testimonials.</div>
      ) : pending.map((t) => (
        <div key={t.id} className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`admin-testimonial-${t.id}`}>
          <div className="flex gap-0.5 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < t.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/15"}`} />
            ))}
          </div>
          <p className="text-[#D4D4D8] text-sm mb-2">"{t.quote}"</p>
          <div className="text-xs text-[#71717A] mb-3">{t.name}{t.company ? ` · ${t.company}` : ""} · {t.industry}</div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => approve(t, false)} data-testid={`approve-testimonial-${t.id}`}
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
            <Button onClick={() => approve(t, true)} variant="outline"
              className="rounded-full h-8 text-xs border-[#D4AF37]/40 text-[#D4AF37]">Approve + Feature</Button>
            <Button onClick={() => remove(t)} variant="ghost" className="rounded-full h-8 text-xs text-red-300"><Trash2 className="w-3.5 h-3.5 mr-1" /> Reject</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Rewards() {
  const [rewards, setRewards] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/referrals/rewards${filter ? `?status=${filter}` : ""}`)
      .then((r) => setRewards(r.data.rewards)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try { await api.post(`/admin/referrals/rewards/${r.reward_id}/approve`); toast.success("Approved & credited"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not approve"); }
  };
  const reverse = async (r) => {
    try { await api.post(`/admin/referrals/rewards/${r.reward_id}/reverse`, { reason: "admin review" }); toast.success("Reversed"); load(); }
    catch (e) { toast.error("Could not reverse"); }
  };
  const adjust = async (r) => {
    const val = window.prompt("New credit amount (USD):", r.credit_usd);
    if (val == null) return;
    try { await api.post(`/admin/referrals/rewards/${r.reward_id}/adjust`, { credit_usd: parseFloat(val) || 0, ai_credits: r.ai_credits });
      toast.success("Adjusted"); load(); }
    catch (e) { toast.error("Could not adjust"); }
  };

  return (
    <div data-testid="admin-rewards">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "pending_approval", "granted", "reversed"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`reward-filter-${s || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize ${filter === s ? "fv-gold-gradient text-[#0A0A0B] font-semibold border-transparent" : "border-white/10 text-[#A1A1AA]"}`}>
            {s ? s.replace("_", " ") : "all"}</button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div> :
       rewards.length === 0 ? <div className="text-center py-10 text-[#52525B]">No rewards found.</div> :
       <div className="space-y-2">
        {rewards.map((r) => (
          <div key={r.reward_id} className="rounded-xl border border-white/8 bg-[#121214] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-reward-${r.reward_id}`}>
            <div className="min-w-0">
              <div className="text-sm text-white flex items-center gap-2">
                {r.kind === "milestone" ? <Award className="w-4 h-4 text-[#D4AF37]" /> : null}
                {r.kind === "milestone" ? `${r.badge} milestone (${r.milestone} refs)` : "Referral reward"}
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${REWARD_STATUS[r.status] || "bg-white/10 text-[#A1A1AA]"}`}>{r.status.replace("_", " ")}</span>
              </div>
              <div className="text-xs text-[#71717A]">{r.referrer_name} ({r.referrer_email}) · ${r.credit_usd}{r.ai_credits ? ` + ${r.ai_credits} AI` : ""}</div>
            </div>
            <div className="flex gap-2">
              {r.status === "pending_approval" && (
                <Button onClick={() => approve(r)} data-testid={`approve-reward-${r.reward_id}`}
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-8 text-xs">Approve</Button>
              )}
              <Button onClick={() => adjust(r)} variant="outline" className="rounded-full h-8 text-xs border-white/15 text-[#D4D4D8]">Adjust</Button>
              {r.status !== "reversed" && (
                <Button onClick={() => reverse(r)} variant="ghost" className="rounded-full h-8 text-xs text-red-300"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reverse</Button>
              )}
            </div>
          </div>
        ))}
       </div>}
    </div>
  );
}

function StatusMgmt() {
  const [form, setForm] = useState({ title: "", message: "", kind: "incident", status: "investigating", severity: "minor", notify: true });
  const [subs, setSubs] = useState({ subscribers: [], stats: { total: 0, active: 0 } });
  const [notifs, setNotifs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/status/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/status/notifications").then((r) => setNotifs(r.data.notifications)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (form.title.trim().length < 4) { toast.error("Enter a clear title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/incidents", form);
      toast.success(`Incident posted${data.notified ? ` — ${data.notified} subscriber(s) notified` : ""}`);
      setForm({ title: "", message: "", kind: "incident", status: "investigating", severity: "minor", notify: true });
      load();
    } catch (e) { toast.error("Could not post incident"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-status-mgmt">
      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <Users className="w-5 h-5 text-[#D4AF37] mb-2" />
          <div className="text-2xl font-heading text-white" data-testid="status-active-subs">{subs.stats.active}</div>
          <div className="text-xs text-[#71717A]">Active subscribers</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <Send className="w-5 h-5 text-[#D4AF37] mb-2" />
          <div className="text-2xl font-heading text-white">{notifs.length}</div>
          <div className="text-xs text-[#71717A]">Notifications sent</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <div className="text-2xl font-heading text-white">{subs.stats.total}</div>
          <div className="text-xs text-[#71717A]">Total (incl. unsubscribed)</div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
        <div className="flex items-center gap-2 mb-3 text-white font-medium"><Megaphone className="w-4 h-4 text-[#D4AF37]" /> Post an incident / maintenance notice</div>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="incident-title"
          placeholder="Title" className="bg-[#0d0d0f] border-white/10 text-white mb-2" />
        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="incident-message"
          placeholder="What's happening / affected services" className="bg-[#0d0d0f] border-white/10 text-white mb-2" />
        <div className="flex gap-2 flex-wrap mb-3">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="incident-kind"
            className="bg-[#0d0d0f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="incident">Incident</option>
            <option value="degradation">Degradation</option>
            <option value="maintenance">Maintenance</option>
          </select>
          <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
            className="bg-[#0d0d0f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="critical">Critical</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
            <input type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })}
              data-testid="incident-notify" className="accent-[#D4AF37]" /> Notify subscribers
          </label>
        </div>
        <Button onClick={create} disabled={busy} data-testid="incident-submit"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">{busy ? "Posting…" : "Post notice"}</Button>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
        <h3 className="text-white font-medium mb-3">Notification history</h3>
        {notifs.length === 0 ? <p className="text-sm text-[#52525B]">No notifications sent yet.</p> :
          <div className="max-h-80 overflow-y-auto">
            {notifs.slice(0, 100).map((n, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0 text-sm" data-testid="status-notif-row">
                <span className="text-[#D4D4D8] truncate">{n.email} · <span className="capitalize text-[#71717A]">{n.kind}</span></span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${n.delivery_status === "sent" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-[#A1A1AA]"}`}>{n.delivery_status}</span>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

export default function AdminTrust() {
  const [tab, setTab] = useState("testimonials");
  return (
    <div data-testid="admin-trust">
      <SubTabs tab={tab} setTab={setTab} />
      {tab === "testimonials" && <Testimonials />}
      {tab === "rewards" && <Rewards />}
      {tab === "status" && <StatusMgmt />}
    </div>
  );
}
